import React from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { FlowNode } from '../../../types/flowBuilder';
import { PencilSquareIcon } from '@heroicons/react/24/outline';

const TextProcessorNode: React.FC<NodeProps<FlowNode['data']>> = ({ data, selected }) => {
  const operation = data.config?.operation || 'uppercase';

  const getOperationIcon = (op: string) => {
    const icons: Record<string, string> = {
      uppercase: 'ABC',
      lowercase: 'abc',
      capitalize: 'Abc',
      trim: '✂️',
      length: '#',
      reverse: '↔️',
      replace: '🔄',
      split: '✂️',
      substring: '📏',
      concat: '+',
      contains: '🔍',
      startsWith: '⏮️',
      endsWith: '⏭️'
    };
    return icons[op] || '📝';
  };

  const getOperationLabel = (op: string) => {
    const labels: Record<string, string> = {
      uppercase: 'Nagybetűs',
      lowercase: 'Kisbetűs',
      capitalize: 'Első betű nagy',
      trim: 'Szóközök eltávolítása',
      length: 'Hossz meghatározás',
      reverse: 'Megfordítás',
      replace: 'Csere',
      split: 'Felosztás',
      substring: 'Részstring',
      concat: 'Összefűzés',
      contains: 'Tartalmaz-e',
      startsWith: 'Kezdődik-e',
      endsWith: 'Végződik-e'
    };
    return labels[op] || op;
  };

  return (
    <div className={`px-4 py-3 shadow-lg rounded-lg bg-gradient-to-r from-amber-400 to-amber-500 border-2 transition-all duration-200 ${
      selected ? 'border-amber-600 shadow-xl scale-105' : 'border-amber-300'
    }`}>
      <div className="flex items-center space-x-3">
        {/* Icon */}
        <div className="flex-shrink-0">
          <div className="w-8 h-8 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
            <PencilSquareIcon className="w-5 h-5 text-white" />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white truncate">
            {data.label}
          </div>
          {data.description && (
            <div className="text-xs text-amber-100 truncate mt-1">
              {data.description}
            </div>
          )}
          
          {/* Operation display */}
          <div className="flex items-center mt-1 space-x-2">
            <span className="text-sm">
              {getOperationIcon(operation)}
            </span>
            <span className="text-xs text-amber-100">
              {getOperationLabel(operation)}
            </span>
          </div>
        </div>

        {/* Status indicator */}
        <div className="flex-shrink-0">
          <div className={`w-3 h-3 rounded-full ${
            data.hasError ? 'bg-red-500' : 'bg-green-400'
          }`} />
        </div>
      </div>

      {/* Error message */}
      {data.hasError && data.errorMessage && (
        <div className="mt-2 text-xs text-red-200 bg-red-500 bg-opacity-20 px-2 py-1 rounded">
          {data.errorMessage}
        </div>
      )}

      {/* Text processing result preview */}
      {data.executionData && (
        <div className="mt-2 text-xs text-amber-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <strong>Eredmény:</strong>
          <div className="mt-1 max-h-16 overflow-y-auto">
            {data.executionData.input && (
              <div className="opacity-75 truncate">
                <span className="font-semibold">Bemenet:</span> "{data.executionData.input}"
              </div>
            )}
            {data.executionData.result !== undefined && (
              <div className="truncate">
                <span className="font-semibold">Kimenet:</span> "{data.executionData.result}"
              </div>
            )}
          </div>
        </div>
      )}

      {/* Configuration preview */}
      {data.config?.searchValue && (
        <div className="mt-2 text-xs text-amber-100 bg-black bg-opacity-20 px-2 py-1 rounded">
          <strong>Beállítás:</strong> {data.config.searchValue}
          {data.config.replaceValue && ` → ${data.config.replaceValue}`}
        </div>
      )}

      {/* Input handle */}
      <Handle
        type="target"
        position={Position.Left}
        className="w-3 h-3 bg-amber-600 border-2 border-white"
      />

      {/* Output handle */}
      <Handle
        type="source"
        position={Position.Right}
        className="w-3 h-3 bg-amber-600 border-2 border-white"
      />
    </div>
  );
};

export default TextProcessorNode;
